import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
} from "@/components/ui/dialog";
import { ChevronLeft, ChevronRight, X, Play } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/contexts/LanguageContext";
import { BibleSchoolMediaItem } from "./BibleSchoolMediaItem";
import { BibleSchoolLightbox } from "./BibleSchoolLightbox";

interface Media {
  id: string;
  title: string;
  description: string | null;
  media_type: string;
  image_url: string;
  video_url: string | null;
  display_order: number;
  course_id: string | null;
  courses?: {
    title: string;
  };
  translations?: Array<{
    language: string;
    title: string;
    description: string | null;
  }>;
}

export const BibleSchoolGalleryDisplay = () => {
  const { language } = useLanguage();
  const [media, setMedia] = useState<Media[]>([]);
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [selectedGallery, setSelectedGallery] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [galleryPages, setGalleryPages] = useState<Record<string, number>>({});
  const itemsPerPage = 6;

  useEffect(() => {
    fetchMedia();
  }, []);

  const fetchMedia = async () => {
    setIsLoading(true);
    const { data, error } = await supabase
      .from("bible_school_media")
      .select(`
        *,
        courses (
          title
        ),
        translations:bible_school_media_translations (
          language,
          title,
          description
        )
      `)
      .order("display_order", { ascending: true });

    if (!error && data) {
      setMedia(data);
    }
    setIsLoading(false);
  };

  const getTranslatedContent = (item: Media) => {
    if (language === "en") {
      return { title: item.title, description: item.description };
    }
    
    const translation = item.translations?.find(t => t.language === language);
    return translation 
      ? { title: translation.title, description: translation.description }
      : { title: item.title, description: item.description };
  };

  const openLightbox = (galleryTitle: string, index: number) => {
    setSelectedGallery(galleryTitle);
    setSelectedIndex(index);
  };

  const closeLightbox = () => {
    setSelectedIndex(null);
    setSelectedGallery(null);
  };

  const goToPrevious = () => {
    if (selectedIndex !== null && selectedGallery && selectedIndex > 0) {
      setSelectedIndex(selectedIndex - 1);
    }
  };

  const goToNext = () => {
    if (selectedIndex !== null && selectedGallery) {
      const galleryMedia = getGalleryMedia(selectedGallery);
      if (selectedIndex < galleryMedia.length - 1) {
        setSelectedIndex(selectedIndex + 1);
      }
    }
  };

  const getGalleryMedia = (title: string) => {
    return media.filter(item => item.title === title);
  };

  const setGalleryPage = (galleryTitle: string, page: number) => {
    setGalleryPages(prev => ({ ...prev, [galleryTitle]: page }));
  };

  const getGalleryPage = (galleryTitle: string) => {
    return galleryPages[galleryTitle] || 1;
  };

  if (isLoading) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">Loading gallery...</p>
      </div>
    );
  }

  if (media.length === 0) {
    return null;
  }

  // Group media by title
  const galleries: Record<string, Media[]> = {};
  media.forEach(item => {
    const { title } = getTranslatedContent(item);
    if (!galleries[title]) {
      galleries[title] = [];
    }
    galleries[title].push(item);
  });

  const selectedMedia = selectedIndex !== null && selectedGallery
    ? getGalleryMedia(selectedGallery)[selectedIndex]
    : null;

  return (
    <section className="py-16 bg-card rounded-2xl mt-12">
      <div className="container mx-auto px-4">
        <h2 className="text-3xl font-bold text-center mb-12">Photo & Video Galleries</h2>
        
        {Object.entries(galleries).map(([galleryTitle, galleryMedia]) => {
          const currentPage = getGalleryPage(galleryTitle);
          const totalPages = Math.ceil(galleryMedia.length / itemsPerPage);
          const startIndex = (currentPage - 1) * itemsPerPage;
          const currentMedia = galleryMedia.slice(startIndex, startIndex + itemsPerPage);

          return (
            <div key={galleryTitle} className="mb-16">
              <h3 className="text-2xl font-semibold mb-6">{galleryTitle}</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {currentMedia.map((item, index) => {
                  const actualIndex = startIndex + index;
                  const { description } = getTranslatedContent(item);
                  return (
                    <BibleSchoolMediaItem
                      key={item.id}
                      item={item}
                      description={description}
                      onClick={() => openLightbox(galleryTitle, actualIndex)}
                    />
                  );
                })}
              </div>

              {/* Pagination Controls for this gallery */}
              {totalPages > 1 && (
                <div className="flex items-center justify-center gap-2 mt-8">
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => setGalleryPage(galleryTitle, currentPage - 1)}
                    disabled={currentPage === 1}
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>

                  <div className="flex gap-2">
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                      <Button
                        key={page}
                        variant={currentPage === page ? "default" : "outline"}
                        size="icon"
                        onClick={() => setGalleryPage(galleryTitle, page)}
                        className="w-10"
                      >
                        {page}
                      </Button>
                    ))}
                  </div>

                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => setGalleryPage(galleryTitle, currentPage + 1)}
                    disabled={currentPage === totalPages}
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              )}
            </div>
          );
        })}

        <Dialog open={selectedIndex !== null} onOpenChange={closeLightbox}>
          <DialogContent className="max-w-5xl p-0">
            {selectedMedia && (() => {
              const { title, description } = getTranslatedContent(selectedMedia);
              return (
                <BibleSchoolLightbox
                  media={selectedMedia}
                  title={title}
                  description={description}
                  canGoPrevious={selectedIndex > 0}
                  canGoNext={selectedGallery ? selectedIndex < getGalleryMedia(selectedGallery).length - 1 : false}
                  onClose={closeLightbox}
                  onPrevious={goToPrevious}
                  onNext={goToNext}
                />
              );
            })()}
          </DialogContent>
        </Dialog>
      </div>
    </section>
  );
};
